import { MODULE_KEYS, type OrgModuleAccess, isModuleTabVisible } from '../constants/moduleAccess.ts';
import { TENANT_PATHS } from './paths.ts';

export type RouteModuleRequirement = {
  moduleKey?: string;
  moduleKeys?: string[];
};

/** Longest-prefix wins. Paths not listed are not module-gated. */
const ROUTE_MODULE_PREFIX_RULES: Array<{ prefix: string } & RouteModuleRequirement> = [
  { prefix: '/risk-control-matrix', moduleKeys: [MODULE_KEYS.RISKS, MODULE_KEYS.CONTROLS] },
  { prefix: TENANT_PATHS.CONTROL_TESTING_TEST_RUNS, moduleKey: MODULE_KEYS.TEST_EXECUTIONS },
  { prefix: TENANT_PATHS.MATERIALITY_DASHBOARD, moduleKey: MODULE_KEYS.MATERIALITY },
  { prefix: TENANT_PATHS.SOX_MAPPING_REPORT, moduleKey: MODULE_KEYS.MATERIALITY },
  { prefix: TENANT_PATHS.SOX_CONTROL_LIBRARY, moduleKey: MODULE_KEYS.CONTROLS },
  { prefix: TENANT_PATHS.SOX_CONTROLS, moduleKey: MODULE_KEYS.CONTROLS },
  { prefix: TENANT_PATHS.SOX_RISKS, moduleKey: MODULE_KEYS.RISKS },
  { prefix: TENANT_PATHS.SOX_SYSTEMS, moduleKey: MODULE_KEYS.SYSTEMS },
  { prefix: TENANT_PATHS.SOX_REPORTS, moduleKey: MODULE_KEYS.REPORTS },
  { prefix: '/sox-cycles', moduleKey: MODULE_KEYS.PROCESSES },
  { prefix: TENANT_PATHS.SOX_DASHBOARD, moduleKey: MODULE_KEYS.PROCESSES },
  { prefix: TENANT_PATHS.SOX_FLOWCHARTS, moduleKey: MODULE_KEYS.PROCESSES },
  { prefix: TENANT_PATHS.MATERIALITY, moduleKey: MODULE_KEYS.MATERIALITY },
  { prefix: TENANT_PATHS.RISK_ASSESSMENT, moduleKey: MODULE_KEYS.MATERIALITY },
  { prefix: TENANT_PATHS.VALIDATE, moduleKey: MODULE_KEYS.SOF },
  { prefix: TENANT_PATHS.ISSUES_MANAGEMENT, moduleKey: MODULE_KEYS.SOX_ISSUES },
  { prefix: '/issues', moduleKey: MODULE_KEYS.SOX_ISSUES },
  { prefix: TENANT_PATHS.SOP_SYNTHESIZER, moduleKey: MODULE_KEYS.SOP_SYNTHESIZER },
  { prefix: '/analytics', moduleKey: MODULE_KEYS.REPORTS },
  { prefix: TENANT_PATHS.COMPLIANCE_REPORT, moduleKey: MODULE_KEYS.SOF },
];

const LANDING_ROUTE_CANDIDATES: Array<{ path: string } & RouteModuleRequirement> = [
  { path: TENANT_PATHS.MATERIALITY_DASHBOARD, moduleKey: MODULE_KEYS.MATERIALITY },
  { path: TENANT_PATHS.SOX_CONTROLS, moduleKey: MODULE_KEYS.CONTROLS },
  { path: TENANT_PATHS.SOX_RISKS, moduleKey: MODULE_KEYS.RISKS },
  { path: '/risk-control-matrix', moduleKeys: [MODULE_KEYS.RISKS, MODULE_KEYS.CONTROLS] },
  { path: TENANT_PATHS.SOX_DASHBOARD, moduleKey: MODULE_KEYS.PROCESSES },
  { path: TENANT_PATHS.SOX_SYSTEMS, moduleKey: MODULE_KEYS.SYSTEMS },
  { path: TENANT_PATHS.CONTROL_TESTING_TEST_RUNS, moduleKey: MODULE_KEYS.TEST_EXECUTIONS },
  { path: TENANT_PATHS.ISSUES_MANAGEMENT, moduleKey: MODULE_KEYS.SOX_ISSUES },
  { path: TENANT_PATHS.VALIDATE, moduleKey: MODULE_KEYS.SOF },
  { path: TENANT_PATHS.MY_DASHBOARD, moduleKey: MODULE_KEYS.REPORTS },
];

function normalizePath(path: string): string {
  if (!path) return '/';
  const base = path.split('?')[0]?.split('#')[0] ?? path;
  if (base.length > 1 && base.endsWith('/')) return base.slice(0, -1);
  return base;
}

export function resolveRouteModuleRequirement(pathname: string): RouteModuleRequirement | null {
  const path = normalizePath(pathname);
  let match: ({ prefix: string } & RouteModuleRequirement) | null = null;
  for (const rule of ROUTE_MODULE_PREFIX_RULES) {
    if (path === rule.prefix || path.startsWith(`${rule.prefix}/`)) {
      if (!match || rule.prefix.length > match.prefix.length) {
        match = rule;
      }
    }
  }
  if (!match) return null;
  if (match.moduleKeys?.length) return { moduleKeys: [...match.moduleKeys] };
  if (match.moduleKey) return { moduleKey: match.moduleKey };
  return null;
}

export function getFirstAllowedTenantPath(access: OrgModuleAccess | undefined): string | null {
  if (!access) return null;
  if (access.unrestricted) return TENANT_PATHS.MATERIALITY_DASHBOARD;
  for (const candidate of LANDING_ROUTE_CANDIDATES) {
    if (isModuleTabVisible(candidate, access)) return candidate.path;
  }
  return null;
}
